import { useEffect } from 'react';
import { useAuthStore } from '@/stores/auth-store';
import { multiTenantApi } from '@/services/api/multi-tenant-api';
import { redirectToAccountSuspended } from '@/services/api/interceptor';

/**
 * Refreshes the tenant facts that the login response baked into the auth store
 * (`subscriptionPlan`, `tenantStatus`) from the server each time the app shell mounts.
 *
 * Without this, a plan change made by a super admin only reaches the customer after
 * they log out and back in, because `useEnterprisePlan` reads the cached login value.
 * A suspension is also caught here: the interceptor redirects on the 403 this call
 * returns, so the customer sees the suspended screen instead of an empty dashboard.
 */
// One in-flight/recent sync per access token: the hook is mounted at the app root
// (every route) and again under ProtectedRoute (re-sync when the shell changes), and
// both must not turn into two identical requests per page load.
let lastSync: { token: string; at: number } | null = null;
const SYNC_DEDUPE_MS = 3000;

export function useTenantSync(): void {
  const tenantId = useAuthStore((s) => s.user?.tenantId);
  const accessToken = useAuthStore((s) => s.tokens?.accessToken);
  const updateUser = useAuthStore((s) => s.updateUser);
  const setTenantSynced = useAuthStore((s) => s.setTenantSynced);

  useEffect(() => {
    if (!tenantId || !accessToken) {
      // Nothing to sync (no tenant, e.g. a platform owner): don't hold gates open.
      setTenantSynced(true);
      return;
    }
    if (lastSync && lastSync.token === accessToken && Date.now() - lastSync.at < SYNC_DEDUPE_MS) {
      return;
    }
    lastSync = { token: accessToken, at: Date.now() };
    let cancelled = false;
    multiTenantApi
      .getCurrentTenant(accessToken)
      .then((tenant) => {
        if (cancelled || !tenant) return;
        const updates: { subscriptionPlan?: string; tenantStatus?: string } = {};
        if (tenant.subscriptionPlan) updates.subscriptionPlan = tenant.subscriptionPlan;
        if (tenant.status) updates.tenantStatus = tenant.status;
        if (Object.keys(updates).length > 0) updateUser(updates);
      })
      .catch((err: unknown) => {
        // The tenant-status filter answers 403 TENANT_SUSPENDED for a suspended or
        // closed tenant on every call. This request is the first one every
        // authenticated shell makes, and it bypasses the shared interceptor, so the
        // redirect has to happen here too — otherwise the user sees a normal-looking
        // page whose every query silently fails.
        const e = err as { status?: number; response?: { errorCode?: string } };
        if (e?.status === 403 || e?.response?.errorCode === 'TENANT_SUSPENDED') {
          updateUser({ tenantStatus: 'suspended' });
          redirectToAccountSuspended();
          return;
        }
        // Anything else keeps the login-time values, which is the previous behaviour.
      })
      .finally(() => {
        if (!cancelled) setTenantSynced(true);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, accessToken, updateUser, setTenantSynced]);
}

export default useTenantSync;
